/**
 * useTodayWebSocket Hook
 *
 * Hook for real-time Today Page updates via WebSocket.
 * Listens to organization-level signal events and invalidates
 * React Query cache when updates are received, triggering automatic refetch.
 *
 * Uses existing signal events rather than Today-specific messages,
 * as per PRD requirement to leverage existing WS /signals endpoint.
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from '../../../../contexts/WebSocketContext';

/**
 * Signal event types that indicate Today metrics may have changed.
 * These events are already published by the backend to the org channel.
 */
const TODAY_RELEVANT_EVENTS = [
  'signal.created',
  'signal.classified',
  'signal.updated',
  'signal.finalized',
] as const;

/**
 * Hook for real-time Today Page updates via WebSocket.
 *
 * Listens to organization-level signal events and invalidates
 * the today-metrics query when relevant events occur, triggering
 * automatic refetch.
 *
 * No explicit subscription is needed - the WebSocket automatically
 * subscribes to the org channel on connection.
 *
 * @example
 * ```tsx
 * function TodayPage() {
 *   useTodayWebSocket();
 *   const { data } = useTodayMetrics();
 *   // Data automatically refreshes on WebSocket updates
 * }
 * ```
 */
export function useTodayWebSocket(): void {
  const queryClient = useQueryClient();
  const { addMessageListener, isConnected } = useWebSocket();

  // Handle incoming WebSocket messages
  const handleMessage = useCallback(
    (message: Record<string, unknown>) => {
      try {
        const messageType = message.type as string;

        // Check if this is a signal event that could affect Today metrics
        if (
          TODAY_RELEVANT_EVENTS.includes(
            messageType as (typeof TODAY_RELEVANT_EVENTS)[number]
          )
        ) {
          // Invalidate the today-metrics query to trigger refetch
          // Note: Query key must match todayKeys.metrics() in useTodayMetrics.ts
          void queryClient.invalidateQueries({ queryKey: ['today', 'metrics'] });

          if (process.env.NODE_ENV === 'development') {
            console.log(`[TodayWebSocket] Refreshing due to: ${messageType}`);
          }
        }
      } catch (error) {
        console.error('[TodayWebSocket] Failed to process message:', error);
      }
    },
    [queryClient]
  );

  // Listen to WebSocket messages on mount
  // No subscription needed - the WebSocket auto-subscribes to org channel
  useEffect(() => {
    if (!isConnected) return;

    // Add message listener
    const removeListener = addMessageListener(handleMessage);

    // Cleanup on unmount
    return () => {
      removeListener();
    };
  }, [isConnected, addMessageListener, handleMessage]);
}
